/**
 * Shared required-field validation for schema-driven (DynamicForm) forms.
 *
 * This is the single source of truth for "are this form's requirements met?"
 * It is used both by DynamicForm (inline Save / Save & Submit) and by any
 * non-form page that submits a record (e.g. the deliverables table row
 * "Submit" action) so that front-end requirements are always enforced
 * pre-API-call, regardless of which UI initiates the submit.
 *
 * Field requirement semantics:
 *   - `required: true`          → required to Save AND Submit
 *   - `requiredToSubmit: true`  → optional to Save, required to Submit
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

interface FieldConfig {
  type?: string;
  name?: string;
  label?: string;
  required?: boolean;
  requiredToSubmit?: boolean;
  fields?: FieldConfig[];
  _parentPath?: string;
  [key: string]: any;
}

// Flatten a schema (or raw blocks array) into a flat list of field configs,
// tagging each with `_parentPath` for nested (repeatableSection) lookups.
// Mirrors DynamicForm's getAllFields traversal exactly.
export const flattenSchemaFields = (schemaOrBlocks: any): FieldConfig[] => {
  const blocks: any[] = Array.isArray(schemaOrBlocks)
    ? schemaOrBlocks
    : schemaOrBlocks?.blocks || [];

  const processField = (f: any, acc: FieldConfig[], parentPath = ''): void => {
    if (!f) return;

    // Groups pass through the parentPath unchanged
    if (f.type === 'group' && Array.isArray(f.fields)) {
      f.fields.forEach((gf: any) => processField(gf, acc, parentPath));
      return;
    }

    // Repeatable sections add their own name to the path
    if (f.type === 'repeatableSection' && Array.isArray(f.fields)) {
      const newPath = parentPath ? `${parentPath}.${f.name}` : f.name;
      (f.fields || []).forEach((nf: any) => processField(nf, acc, newPath));
      return;
    }

    acc.push({ ...f, _parentPath: parentPath });
  };

  const walk = (arr: any[], acc: FieldConfig[] = []): FieldConfig[] => {
    (arr || []).forEach((blk: any) => {
      if (blk?.type === 'columnLayout' && Array.isArray(blk.columns)) {
        blk.columns.forEach((col: any) => {
          (col?.fields || []).forEach((cf: any) => processField(cf, acc, ''));
        });
        return;
      }

      if (blk?.type === 'repeatableSection' || blk?.type === 'group') {
        processField(blk, acc, '');
        return;
      }

      (blk?.fields || []).forEach((f: any) => processField(f, acc, ''));
    });
    return acc;
  };

  return walk(blocks, []);
};

// Whether a field must be filled for the current action.
const isFieldRequired = (field: FieldConfig, forSubmit: boolean): boolean =>
  Boolean(field?.required || (forSubmit && field?.requiredToSubmit));

// Given an already-flattened field list, return the labels of required fields
// that are missing/empty for the given action.
export const getMissingRequiredFieldsFromFields = (
  fields: FieldConfig[],
  formData: any,
  forSubmit = true,
): string[] => {
  const missingFields: string[] = [];

  const isFilled = (value: any): boolean => {
    return (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0) &&
      !(
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).length === 0
      )
    );
  };

  // Recursive helper to validate nested required fields.
  // Returns: { isValid, allFilled, hasAnyRealItem }
  const checkNestedField = (
    obj: any,
    pathParts: string[],
    fieldName: string,
    depth = 0,
  ): { isValid: boolean; allFilled: boolean; hasAnyRealItem: boolean } => {
    if (!pathParts || pathParts.length === 0) {
      const value = obj?.[fieldName];
      const filled = isFilled(value);
      return { isValid: filled, allFilled: filled, hasAnyRealItem: true };
    }

    let current = obj;

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      current = current?.[part];

      if (!current) {
        return { isValid: false, allFilled: false, hasAnyRealItem: false };
      }

      // If current is an array (RepeatableSection), validate ALL non-placeholder items
      if (Array.isArray(current)) {
        const remainingParts = pathParts.slice(i + 1);

        let hasAnyRealItem = false;
        let allRealItemsFilled = true;

        for (let j = 0; j < current.length; j++) {
          const item = current[j];

          if (!item || typeof item !== 'object') {
            continue;
          }

          if (item.__placeholder) {
            continue;
          }

          hasAnyRealItem = true;
          const result = checkNestedField(item, remainingParts, fieldName, depth + 1);

          if (!result.allFilled) {
            allRealItemsFilled = false;
          }
        }

        const isValid = !hasAnyRealItem || (hasAnyRealItem && allRealItemsFilled);
        return { isValid, allFilled: allRealItemsFilled, hasAnyRealItem };
      }
    }

    const finalValue = current?.[fieldName];
    const filled = isFilled(finalValue);
    return { isValid: filled, allFilled: filled, hasAnyRealItem: true };
  };

  // Special check for estimateTable fields:
  // 1. At least one line item must have data
  // 2. All non-empty line items must have all required fields filled
  const validateEstimateTable = (value: any): { filled: boolean; complete: boolean } => {
    if (!Array.isArray(value) || value.length === 0) return { filled: false, complete: false };

    let hasAtLeastOneFilledRow = false;
    let allFilledRowsComplete = true;

    for (const estimate of value) {
      const lineItems = estimate?.lineItems;
      if (!Array.isArray(lineItems)) continue;

      for (const item of lineItems) {
        const hasAnyData =
          (item.description && String(item.description).trim().length > 0) ||
          (item.quantity && Number(item.quantity) > 0) ||
          (item.unitCost && Number(item.unitCost) > 0) ||
          (item.authorizedIndirectCosts && Number(item.authorizedIndirectCosts) > 0);

        if (!hasAnyData) continue; // skip empty rows

        hasAtLeastOneFilledRow = true;

        const hasDescription = item.description && String(item.description).trim().length > 0;
        const hasQuantity = item.quantity && Number(item.quantity) > 0;
        const hasUnitCost = item.unitCost && Number(item.unitCost) > 0;
        const hasIndirect = item.authorizedIndirectCosts != null && String(item.authorizedIndirectCosts).trim() !== '';

        if (!hasDescription || !hasQuantity || !hasUnitCost || !hasIndirect) {
          allFilledRowsComplete = false;
        }
      }
    }

    return { filled: hasAtLeastOneFilledRow, complete: allFilledRowsComplete };
  };

  (fields || []).forEach((field) => {
    if (isFieldRequired(field, forSubmit)) {
      if (field.type === 'estimateTable') {
        const value = formData?.[field.name as string];
        const result = validateEstimateTable(value);
        if (!result.filled) {
          missingFields.push(field.label || field.name || '');
        } else if (!result.complete) {
          missingFields.push(`${field.label || field.name} (incomplete rows)`);
        }
        return;
      }

      const pathParts = field._parentPath ? field._parentPath.split('.') : [];
      const result = checkNestedField(formData, pathParts, field.name as string, 0);

      if (!result.isValid) {
        missingFields.push(field.label || field.name || '');
      }
    }
  });

  return missingFields;
};

// Convenience: validate a full schema (or blocks array) against form data.
export const getMissingRequiredFields = (
  schemaOrBlocks: any,
  formData: any,
  forSubmit = true,
): string[] =>
  getMissingRequiredFieldsFromFields(
    flattenSchemaFields(schemaOrBlocks),
    formData,
    forSubmit,
  );
